import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CustomTabComponent } from '../../../components/ui/custom-tab/custom-tab.component';
import {
  SearchableSelectComponent,
  SelectItem,
} from '../../../components/ui/searchable-select/searchable-select.component';
import { Router } from '@angular/router';
import { Test } from '../../../core/interfaces/test';
import { TestService } from '../../../core/services/test.service';
import { CommonModule } from '@angular/common';
import { LoaderComponent } from '../../../components/ui/loader/loader.component';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { SwalService } from '../../../core/services/utils/swal.service';
import { AuthService } from '../../../core/services/utils/auth.service';
import { ButtonComponent } from '../../../components/ui/button/button.component';
import { SyncfusionL10nService } from '../../utils/syncfusion-l10n.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { catchError, filter, finalize, takeUntil, tap } from 'rxjs/operators';
import { LanguageService } from '../../../core/services/utils/language.service';
import { LearningService } from '../../../core/services/learning-path.service';
import { Subject as RxJsSubject } from 'rxjs';
import { LearningPath } from '../../../core/interfaces/learning-path';
import {
  ProfessorLearningPathLightDto,
  ProfessorSubjectLightDto,
} from '../../../core/interfaces/professor';
import { Subject } from '../../../core/interfaces/subject';
import { FormsModule } from '@angular/forms';
import { AssignTestModalComponent } from '../../../components/ui/views/assign-test-modal/assign-test-modal.component';

@Component({
  selector: 'app-tests-data',
  templateUrl: 'tests-tab.component.html',
  styleUrls: ['tests-tab.component.css'],
  standalone: true,
  imports: [
    CustomTabComponent,
    SearchableSelectComponent,
    CommonModule,
    LoaderComponent,
    TranslateModule,
    FormsModule,
    AssignTestModalComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class TestsTabComponent implements OnInit, OnDestroy {
  /**
   * Test dont on compose l'affectation. `null` ferme la modale.
   *
   * <p>La modale existait sans être branchée nulle part : elle était écrite,
   * traduite à moitié, et inatteignable. C'est par ici qu'un professeur envoie
   * un test à un groupe — y compris à un groupe issu du classement
   * automatique, qui figure dans sa liste comme les autres.</p>
   */
  readonly assignTarget = signal<Test | null>(null);

  openAssign(test: Test): void {
    this.assignTarget.set(test);
  }

  subjects: ProfessorSubjectLightDto[] = [];
  learningPaths: ProfessorLearningPathLightDto[] = [];
  selectedPathId: number | null = null;
  selectedSubjectId: number | null = null;
  isRTL = false;
  currentLocale = 'ar';
  isLoading = false;
  isLoadingPaths = false;
  isLoadingSubjects = false;
  isSaving = false;
  error: string | null = null;
  public data: Test[] = [];
  hasError = false;
  errorMessage = '';
  public pageSettings!: Object;
  public toolbar!: string[];
  selectedTest: Test | null = null;
  private testsSub?: Subscription;
  private langChangeSub?: Subscription;
  public enableRTL = true;
  testModeTranslations: { [key: string]: string } = {};
  public translatedHeaders = {
    title: '',
    mode: '',
    count: '',
    subject: '',
    path: '',
    startDate: '',
    endDate: '',
    actions: '',
  };

  /** Colonnes pour la table partagée (via app-custom-tab). */
  public columns: any[] = [];

  /** Tous les tests du professeur ; le filtrage se fait côté client. */
  allTests: Test[] = [];
  /** Taxonomie (parcours + matières) : options de filtre + colonne parcours. */
  taxonomy: LearningPath[] = [];
  private subjectPath = new Map<number, { pathId: number; pathTitle: string }>();
  /** Type de test filtré (aligné sur la création) ; null = tous. */
  selectedMode: string | null = null;
  readonly modes = ['exam', 'evaluation', 'practice'];

  private router = inject(Router);
  private testService = inject(TestService);
  private translate = inject(TranslateService);
  private swal = inject(SwalService);
  private authService = inject(AuthService);
  private syncfusionL10n = inject(SyncfusionL10nService);
  private languageService = inject(LanguageService);
  private learningService = inject(LearningService);
  private professorId: string | null = null;
  private destroy$ = new RxJsSubject<void>();

  async ngOnInit(): Promise<void> {
    this.professorId = await this.authService.getUserId();
    this.initializeComponent();
  }

  private async initializeComponent(): Promise<void> {
    try {
      // Set initial language
      const lang = this.languageService.getCurrentLang();
      this.setGridLanguage(lang);
      await this.loadTranslations();

      // Subscribe to language changes
      this.langChangeSub = this.translate.onLangChange.subscribe(
        async (event: LangChangeEvent) => {
          this.setGridLanguage(event.lang);
          await this.loadTranslations();
        },
      );

      // Initialize grid settings
      this.initGridSettings();

      // Charge tous les tests + la taxonomie ; le filtrage est côté client.
      this.loadTaxonomy();
      this.loadAllTests();
    } catch (error) {
      console.error('❌ Error initializing component:', error);
      this.handleError(error);
    }
  }

  /** Tous les tests du professeur (scope JWT), une seule fois. */
  loadAllTests(): void {
    this.isLoading = true;
    this.hasError = false;
    this.testService
      .getByTestsProfessorId()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (res) => {
          this.allTests = res?.status === 'SUCCESS' ? (res.data ?? []) : [];
        },
        error: (error) => {
          console.error('❌ Error loading tests:', error);
          this.hasError = true;
          this.errorMessage = error?.message ?? '';
        },
      });
  }

  retryAll(): void {
    this.loadAllTests();
  }

  async loadTranslations(): Promise<void> {
    try {
      const translations = await firstValueFrom(
        this.translate.get([
          'DATA_TABLE.TITLE',
          'DATA_TABLE.MODE',
          'DATA_TABLE.QUESTION_COUNT',
          'DATA_TABLE.SUBJECT',
          'DATA_TABLE.LEARNING_PATH',
          'DATA_TABLE.START_DATE',
          'DATA_TABLE.END_DATE',
          'DATA_TABLE.ACTIONS',
          'TEST_MODE.EXAM',
          'TEST_MODE.PRACTICE',
          'TEST_MODE.EVALUATION',
        ]),
      );

      this.translatedHeaders = {
        title: translations['DATA_TABLE.TITLE'],
        mode: translations['DATA_TABLE.MODE'],
        count: translations['DATA_TABLE.QUESTION_COUNT'],
        subject: translations['DATA_TABLE.SUBJECT'],
        path: translations['DATA_TABLE.LEARNING_PATH'],
        startDate: translations['DATA_TABLE.START_DATE'],
        endDate: translations['DATA_TABLE.END_DATE'],
        actions: translations['DATA_TABLE.ACTIONS'],
      };

      this.testModeTranslations = {
        exam: translations['TEST_MODE.EXAM'],
        practice: translations['TEST_MODE.PRACTICE'],
        evaluation: translations['TEST_MODE.EVALUATION'],
      };
      this.buildColumns();
    } catch (error) {
      console.error('❌ Error loading translations:', error);
      // Set default values if translations fail
      this.translatedHeaders = {
        title: 'Title',
        mode: 'Mode',
        count: 'Questions',
        subject: 'Subject',
        path: 'Parcours',
        startDate: 'Start Date',
        endDate: 'End Date',
        actions: 'Actions',

      };
      this.buildColumns();
    }
  }

  /** Colonnes de la table partagée ; mode/dates rendus via `cellTemplates`. */
  private buildColumns(): void {
    const h = this.translatedHeaders;
    this.columns = [
      { key: 'title', field: 'title', headerText: h.title },
      { key: 'testMode', field: 'testMode', headerText: h.mode },
      { key: 'questionsCount', field: 'questionsCount', headerText: h.count, textAlign: 'Center' },
      { key: 'subjectName', field: 'subjectName', headerText: h.subject },
      { key: 'path', headerText: h.path, hideBelow: 'lg' },
      { key: 'startDate', field: 'startDate', headerText: h.startDate, hideBelow: 'md' },
      { key: 'endDate', field: 'endDate', headerText: h.endDate, hideBelow: 'md' },
      { key: 'actions', headerText: h.actions, template: 'actionTemplate', textAlign: 'Center' },
    ];
  }

  setGridLanguage(lang: string): void {
    this.currentLocale = lang;
    this.enableRTL = lang === 'ar';
  }

  /** Options du filtre parcours (avec « Tous »). */
  get pathOptions(): SelectItem[] {
    return [
      { id: 0, label: this.translate.instant('ADMIN.STATUS_ALL') },
      ...this.taxonomy.map((p) => ({ id: p.id, label: p.title })),
    ];
  }
  /** Options du filtre matière (selon le parcours choisi, avec « Toutes »). */
  get subjectOptions(): SelectItem[] {
    const subs =
      this.selectedPathId && this.selectedPathId !== 0
        ? this.taxonomy.find((p) => p.id === this.selectedPathId)?.subjects ?? []
        : this.taxonomy.flatMap((p) => p.subjects ?? []);
    return [
      { id: 0, label: this.translate.instant('ADMIN.STATUS_ALL') },
      ...subs.map((s) => ({ id: s.id, label: s.name })),
    ];
  }

  testDetail(testId: number): void {
    if (!testId) {
      console.error('❌ Invalid test ID');
      return;
    }
    this.router.navigate(['/teacher/test', testId]);
  }

  togglePublish(test: Test): void {
    if (!test || !test.id) {
      console.error('Invalid test');
      return;
    }

    const isPublished = test.status === 'published';
    this.swal.confirmPublishToggle(isPublished, async () => {
      const newStatus = isPublished ? 'draft' : 'published';

      try {
        const response = await firstValueFrom(
          this.testService.updateStatus(test.id, newStatus)
        );

        if (response.status === 'SUCCESS') {
          test.status = newStatus;
          this.swal.success(this.translate.instant('SWAL.UPDATED'));
        } else {
          throw new Error('Update failed');
        }
      } catch (error) {
        console.error('❌ Error updating status', error);
        this.swal.error(this.translate.instant('SWAL.ERROR'));
      }
    });
  }

  editTest(testId: number): void {
    if (!testId) {
      console.error('❌ Invalid test ID');
      return;
    }
    this.router.navigate(['/teacher/test/update', testId]);
  }

  async onDelete(row: any): Promise<void> {
    if (!row || !row.id) {
      console.error('❌ Invalid row data for deletion');
      return;
    }

    try {
      const message = this.translate.instant('SWAL.DELETE_CONFIRM');

      this.swal.confirm(message, async () => {
        try {
          const response = await firstValueFrom(
            this.testService.delete(row.id),
          );

          if (response.status === 'SUCCESS') {
            this.allTests = this.allTests.filter((test) => test.id !== row.id);
            this.swal.success(this.translate.instant('SWAL.DELETED'));
          } else {
            throw new Error('Delete operation failed');
          }
        } catch (error) {
          console.error('❌ Error deleting test:', error);
          this.swal.error(this.translate.instant('SWAL.DELETE_ERROR'));
        }
      });
    } catch (error) {
      console.error('❌ Error in delete confirmation:', error);
    }
  }

  private initGridSettings(): void {
    this.pageSettings = {
      pageCount: 5,
      pageSize: 10,
      pageSizes: [10, 20, 50, 100],
      enableQueryString: false,
      template: null,
    };
    this.toolbar = ['Search'];
  }

  translateTestMode(testMode: string): string {
    if (!testMode) return '—';
    return this.testModeTranslations[testMode] || testMode;
  }

  private handleError(error: any): void {
    this.isLoading = false;
    this.hasError = true;
    this.errorMessage =
      error?.message || 'An error occurred while loading data';
    console.error('❌ Component error:', error);

    // Optionally show error to user
    const errorMsg = this.translate.instant('ERRORS.LOAD_DATA_ERROR');
    this.swal.error(errorMsg);
  }

  // Retry loading data
  retryLoad(): void {
    this.loadAllTests();
  }

  /**
   * Taxonomie (parcours + matières), en deux lectures qui ne disent pas la
   * même chose.
   *
   * <p>Les **filtres** proposent ce qu'on peut choisir : le périmètre de
   * l'utilisateur, matières actives seulement. La **colonne parcours**, elle,
   * nomme des tests qui existent déjà : un test dont la matière a été
   * désactivée depuis garde son parcours, et reste trouvable par lui. Elle se
   * lit donc dans le référentiel entier.</p>
   */
  loadTaxonomy(): void {
    this.isLoadingPaths = true;
    this.learningService
      .getScopedLearningPaths(this.professorId, this.authService.isAdmin(), true, false)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingPaths = false)),
      )
      .subscribe({
        next: (res) => (this.taxonomy = res?.data ?? []),
        error: () => (this.taxonomy = []),
      });

    this.learningService
      .getReferential()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.subjectPath.clear();
          for (const p of res?.data ?? []) {
            for (const s of p.subjects ?? []) {
              this.subjectPath.set(s.id, { pathId: p.id, pathTitle: p.title });
            }
          }
        },
        // Sans référentiel, la colonne affiche un tiret : la liste reste lisible.
        error: () => this.subjectPath.clear(),
      });
  }

  onPathChange(pathId: number | null): void {
    this.selectedPathId = pathId ?? null;
    // Changer de parcours réinitialise la matière (revient à « toutes »).
    this.selectedSubjectId = null;
  }

  onSubjectChange(subjectId: number | null): void {
    this.selectedSubjectId = subjectId ?? null;
  }

  onModeChange(mode: string): void {
    this.selectedMode = this.selectedMode === mode ? null : mode;
  }

  /** Tests après filtre parcours / matière / type (0 = tous). */
  get filteredTests(): Test[] {
    const pid = this.selectedPathId;
    const sid = this.selectedSubjectId;
    const mode = this.selectedMode;
    return this.allTests.filter((t) => {
      if (mode && t.testMode !== mode) return false;
      if (sid && sid !== 0) return t.subjectId === sid;
      if (pid && pid !== 0)
        return this.subjectPath.get(t.subjectId)?.pathId === pid;
      return true;
    });
  }

  pathTitleFor(subjectId: number): string {
    return this.subjectPath.get(subjectId)?.pathTitle ?? '—';
  }

  /** Clé i18n du type de test — alignée sur l'écran de création. */
  modeKey(mode: string): string {
    return `TEST_BUILDER.MODE_${(mode ?? '').toUpperCase()}`;
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to prevent memory leaks
    this.testsSub?.unsubscribe();
    this.langChangeSub?.unsubscribe();
    console.log('🧹 Component destroyed and subscriptions cleaned up');
  }

  getSelectedData(): {
    selectedPathId: number | null;
    selectedSubjectId: number | null;
    learningPathTitle: string;
    subjectName: string;
  } {
    const selectedPath = this.learningPaths.find(
      (p) => p.id === Number(this.selectedPathId),
    );
    const selectedSubject = this.subjects.find(
      (s) => s.id === Number(this.selectedSubjectId),
    );

    console.log('Getting selected data:', {
      selectedPathId: this.selectedPathId,
      selectedSubjectId: this.selectedSubjectId,
      selectedPath,
      selectedSubject,
      allPaths: this.learningPaths,
      allSubjects: this.subjects,
    });

    return {
      selectedPathId: this.selectedPathId ? Number(this.selectedPathId) : null,
      selectedSubjectId: this.selectedSubjectId
        ? Number(this.selectedSubjectId)
        : null,
      learningPathTitle: selectedPath?.title || '',
      subjectName: selectedSubject?.name || '',
    };
  }
}
